import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { fetchUserPreferences } from "../APIs/UserPreferences/GetUserPreferences";
import { saveUserPreferences } from "../APIs/UserPreferences/SaveUserPreferences";
import { ApiError, errorHandler } from "../APIs/errorHandler";
import { EnquiriesSortOption, FilterOptions } from "../types/enquiries";
import {
  DEFAULT_USER_PREFERENCES,
  EvoFeedSubscriptionKey,
  EvoFeedSubscriptions,
  FilterPreset,
  isRelativeDateRange,
  RelativeDateRange,
  resolveRelativeDateRange,
  UserPreferences
} from "../types/userPreferences";
import { CollinsUserContext } from "./CollinsUserContext";

export type PresetSnapshot = {
  filters: FilterOptions;
  sort?: EnquiriesSortOption;
  query?: string;
  /**
   * Store the date range as a token resolved when the preset is applied,
   * rather than freezing the dates it happens to hold today.
   */
  relativeDateRange?: RelativeDateRange;
};

export type PresetUpdates = {
  name: string;
  filters: FilterOptions;
  sort?: EnquiriesSortOption;
  query?: string;
  pinned?: boolean;
  order?: number;
  /** Undefined clears the token and returns the preset to fixed dates. */
  relative_date_range?: RelativeDateRange;
};

export type UserPreferencesContextType = {
  preferences: UserPreferences;
  isLoading: boolean;
  isSaving: boolean;
  hasLoadError: boolean;
  /**
   * True once the initial fetch for the current user has settled (resolved or
   * failed). Consumers that write derived data must wait for this, otherwise
   * they race the initial load and can persist defaults over server state.
   */
  hasLoadedPreferences: boolean;
  loadPreferences: () => Promise<void>;
  updateEvoFeedSubscription: (
    key: EvoFeedSubscriptionKey,
    value: boolean
  ) => void;
  updateAndSaveSubscription: (
    key: EvoFeedSubscriptionKey,
    value: boolean
  ) => Promise<boolean>;
  updateAndSaveLocale: (locale: string) => Promise<boolean>;
  updateAndSaveLanguage: (language: string) => Promise<boolean>;
  savePreferences: () => Promise<boolean>;
  savePreset: (name: string, snapshot: PresetSnapshot) => Promise<boolean>;
  applyPreset: (id: string) => PresetSnapshot | null;
  deletePreset: (id: string) => Promise<boolean>;
  renamePreset: (id: string, name: string) => Promise<boolean>;
  updatePreset: (id: string, updates: PresetUpdates) => Promise<boolean>;
  setPresetPinned: (id: string, pinned: boolean) => Promise<boolean>;
  reorderPresets: (orderedIds: string[]) => Promise<boolean>;
  toggleEnquiryPinnedNotification: (filterValue: string) => Promise<boolean>;
  mergeEnquiryPinnedNotifications: (filterValues: string[]) => Promise<boolean>;
};

export const UserPreferencesContext = createContext<UserPreferencesContextType>(
  {
    preferences: DEFAULT_USER_PREFERENCES,
    isLoading: false,
    isSaving: false,
    hasLoadError: false,
    hasLoadedPreferences: false,
    loadPreferences: async () => {},
    updateEvoFeedSubscription: () => {},
    updateAndSaveSubscription: async () => false,
    updateAndSaveLocale: async () => false,
    updateAndSaveLanguage: async () => false,
    savePreferences: async () => false,
    savePreset: async () => false,
    applyPreset: () => null,
    deletePreset: async () => false,
    renamePreset: async () => false,
    updatePreset: async () => false,
    setPresetPinned: async () => false,
    reorderPresets: async () => false,
    toggleEnquiryPinnedNotification: async () => false,
    mergeEnquiryPinnedNotifications: async () => false
  }
);

/**
 * Presets written by the short-lived seeding of legacy's rail shortcuts.
 *
 * That seeding materialised Confirmed bookings / Today / Next 3 days into
 * every user's saved presets. It has been removed — those three are
 * navigation rendered from a constant in the rail — but the records it
 * already wrote are still on the server, so the rail listed each of them
 * twice: once as a fixed shortcut and once as the operator's "own" preset,
 * complete with edit and delete controls it should never have had.
 *
 * Matching on the id is exact and cannot catch a real preset: a user-created
 * one is `preset-${crypto.randomUUID()}`, so these three literals are
 * unreachable by any other route.
 *
 * Stripped on read rather than written back. A cleanup write would mean every
 * user silently saving on first load, which is the same class of thing that
 * caused this — and the read filter is idempotent, so the rows can sit
 * harmlessly in storage until something else touches them.
 */
const ORPHANED_SEED_PRESET_IDS: ReadonlySet<string> = new Set([
  "preset-legacy-confirmed-bookings",
  "preset-legacy-today",
  "preset-legacy-next-3-days"
]);

/**
 * Report a preferences load failure. A 401 after the API layer's own refresh
 * retry is already recorded as a telemetry warning — flagging the load error
 * keeps defaults rendered (and save disabled, so defaults can't overwrite
 * saved preferences) without a second, user-facing error report.
 */
const reportPreferencesLoadError = (error: unknown): void => {
  if (error instanceof ApiError && error.status === 401) {
    return;
  }
  // Fire-and-forget: load state is already set and must not wait on reporting.
  void errorHandler(error, "Failed to load user preferences");
};

const mergeWithDefaults = (prefs: UserPreferences): UserPreferences => ({
  ...DEFAULT_USER_PREFERENCES,
  ...prefs,
  evo_feed_subscriptions: {
    ...DEFAULT_USER_PREFERENCES.evo_feed_subscriptions,
    ...prefs.evo_feed_subscriptions
  },
  enquiry_filter_presets: (prefs.enquiry_filter_presets ?? []).filter(
    (preset) => !ORPHANED_SEED_PRESET_IDS.has(preset.id)
  ),
  enquiry_pinned_notifications: prefs.enquiry_pinned_notifications ?? []
});

/**
 * Expands a stored preset into the filters to apply right now.
 *
 * A preset with no `relative_date_range` is returned verbatim — that is every
 * preset saved before tokens existed, and any range the user deliberately
 * fixed. A preset with one has its date bounds recomputed from the local
 * clock, so "Next 3 days" means the next three days on every day it is used.
 *
 * An unrecognised token (a newer client wrote it, or the value was corrupted)
 * falls back to the stored dates rather than throwing: this is server-held
 * user data and must survive being read by an older build.
 */
export const resolvePresetSnapshot = (
  preset: FilterPreset,
  now: Date = new Date()
): PresetSnapshot => {
  const base: PresetSnapshot = {
    filters: preset.filters,
    sort: preset.sort,
    query: preset.query
  };

  if (!isRelativeDateRange(preset.relative_date_range)) return base;

  return {
    ...base,
    relativeDateRange: preset.relative_date_range,
    filters: {
      ...preset.filters,
      ...resolveRelativeDateRange(preset.relative_date_range, now)
    }
  };
};

export const UserPreferencesContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const { user: collinsUser } = useContext(CollinsUserContext);
  const [preferences, setPreferences] = useState<UserPreferences>(
    DEFAULT_USER_PREFERENCES
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [hasLoadedPreferences, setHasLoadedPreferences] = useState(false);

  const loadPreferences = useCallback(async () => {
    if (!collinsUser?._id) return;

    setIsLoading(true);
    setHasLoadError(false);
    try {
      const userPreferences = await fetchUserPreferences(collinsUser._id);

      if (userPreferences) {
        setPreferences(mergeWithDefaults(userPreferences));
      }
    } catch (error) {
      setHasLoadError(true);
      reportPreferencesLoadError(error);
    } finally {
      setIsLoading(false);
      setHasLoadedPreferences(true);
    }
  }, [collinsUser?._id]);

  const updateEvoFeedSubscription = useCallback(
    (key: EvoFeedSubscriptionKey, value: boolean) => {
      setPreferences((prev) => {
        const updatedSubscriptions: EvoFeedSubscriptions = {
          ...prev.evo_feed_subscriptions,
          [key]: value
        };

        // If subscribing to all auto-confirmed bookings,
        // force same-day auto-confirmed bookings to true as well
        if (key === "new_autoconfirmed_bookings" && value) {
          updatedSubscriptions.new_same_day_autoconfirmed_bookings = true;
        }

        return {
          ...prev,
          evo_feed_subscriptions: updatedSubscriptions
        };
      });
    },
    []
  );

  const savePreferences = useCallback(async (): Promise<boolean> => {
    setIsSaving(true);
    try {
      const savedPreferences = await saveUserPreferences(
        collinsUser?._id ?? "",
        preferences
      );
      setPreferences(mergeWithDefaults(savedPreferences));
      return true;
    } catch (error) {
      errorHandler(error, "Failed to save user preferences");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [collinsUser?._id, preferences]);

  const optimisticSave = useCallback(
    async (
      updater: (prev: UserPreferences) => UserPreferences,
      errorMessage: string
    ): Promise<boolean> => {
      let previousPreferences: UserPreferences | undefined;

      const updatedPreferences = await new Promise<UserPreferences>(
        (resolve) => {
          setPreferences((prev) => {
            previousPreferences = prev;
            const next = updater(prev);
            resolve(next);
            return next;
          });
        }
      );

      setIsSaving(true);

      try {
        const savedPreferences = await saveUserPreferences(
          collinsUser?._id ?? "",
          updatedPreferences
        );
        // Layer the server's echo over what we just sent, rather than
        // replacing it. The preferences endpoint does not necessarily return
        // every field it was given, and a field missing from the response is
        // indistinguishable from one the server has never heard of — so a
        // straight replace silently reverted the write. That is what made a
        // pinned notification appear and then vanish a moment later.
        setPreferences(
          mergeWithDefaults({ ...updatedPreferences, ...savedPreferences })
        );
        return true;
      } catch (error) {
        if (previousPreferences) {
          setPreferences(previousPreferences);
        }
        errorHandler(error, errorMessage);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [collinsUser?._id]
  );

  const updateAndSaveSubscription = useCallback(
    (key: EvoFeedSubscriptionKey, value: boolean) =>
      optimisticSave((prev) => {
        const updatedSubscriptions: EvoFeedSubscriptions = {
          ...prev.evo_feed_subscriptions,
          [key]: value
        };

        if (key === "new_autoconfirmed_bookings" && value) {
          updatedSubscriptions.new_same_day_autoconfirmed_bookings = true;
        }

        return { ...prev, evo_feed_subscriptions: updatedSubscriptions };
      }, "Failed to save user preferences"),
    [optimisticSave]
  );

  const updateAndSaveLocale = useCallback(
    (locale: string) =>
      optimisticSave(
        (prev) => ({ ...prev, locale }),
        "Failed to save locale preference"
      ),
    [optimisticSave]
  );

  const updateAndSaveLanguage = useCallback(
    (language: string) =>
      optimisticSave(
        (prev) => ({ ...prev, language }),
        "Failed to save English spelling preference"
      ),
    [optimisticSave]
  );

  const savePreset = useCallback(
    (name: string, snapshot: PresetSnapshot) => {
      const newPreset: FilterPreset = {
        id: `preset-${crypto.randomUUID()}`,
        name,
        filters: snapshot.filters,
        sort: snapshot.sort,
        query: snapshot.query,
        // The absolute dates stay in `filters` as written; the token takes
        // precedence when the preset is applied.
        relative_date_range: snapshot.relativeDateRange
      };
      return optimisticSave(
        (prev) => ({
          ...prev,
          enquiry_filter_presets: [
            ...(prev.enquiry_filter_presets ?? []),
            newPreset
          ]
        }),
        "Failed to save filter preset"
      );
    },
    [optimisticSave]
  );

  const applyPreset = useCallback(
    (id: string): PresetSnapshot | null => {
      const preset = preferences.enquiry_filter_presets?.find(
        (p) => p.id === id
      );
      if (!preset) return null;
      // Resolved here, at click time, against the operator's local clock.
      return resolvePresetSnapshot(preset);
    },
    [preferences.enquiry_filter_presets]
  );

  const deletePreset = useCallback(
    (id: string) =>
      optimisticSave(
        (prev) => ({
          ...prev,
          enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).filter(
            (p) => p.id !== id
          )
        }),
        "Failed to delete filter preset"
      ),
    [optimisticSave]
  );

  const renamePreset = useCallback(
    (id: string, name: string) =>
      optimisticSave(
        (prev) => ({
          ...prev,
          enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).map(
            (p) => (p.id === id ? { ...p, name } : p)
          )
        }),
        "Failed to rename filter preset"
      ),
    [optimisticSave]
  );

  const updatePreset = useCallback(
    (id: string, updates: PresetUpdates) =>
      optimisticSave(
        (prev) => ({
          ...prev,
          enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).map(
            (p) => (p.id === id ? { ...p, ...updates } : p)
          )
        }),
        "Failed to update filter preset"
      ),
    [optimisticSave]
  );

  const setPresetPinned = useCallback(
    (id: string, pinned: boolean) =>
      optimisticSave(
        (prev) => ({
          ...prev,
          enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).map(
            (p) => (p.id === id ? { ...p, pinned } : p)
          )
        }),
        "Failed to pin filter preset"
      ),
    [optimisticSave]
  );

  const reorderPresets = useCallback(
    (orderedIds: string[]) =>
      optimisticSave(
        (prev) => ({
          ...prev,
          enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).map(
            (p) => {
              const index = orderedIds.indexOf(p.id);
              return index === -1 ? p : { ...p, order: index };
            }
          )
        }),
        "Failed to reorder filter presets"
      ),
    [optimisticSave]
  );

  const toggleEnquiryPinnedNotification = useCallback(
    (filterValue: string) =>
      optimisticSave((prev) => {
        const current = prev.enquiry_pinned_notifications ?? [];
        return {
          ...prev,
          enquiry_pinned_notifications: current.includes(filterValue)
            ? current.filter((value) => value !== filterValue)
            : [...current, filterValue]
        };
      }, "Failed to save pinned notifications"),
    [optimisticSave]
  );

  const mergeEnquiryPinnedNotifications = useCallback(
    (filterValues: string[]) =>
      optimisticSave((prev) => {
        const current = prev.enquiry_pinned_notifications ?? [];
        const merged = Array.from(new Set(current.concat(filterValues)));
        return { ...prev, enquiry_pinned_notifications: merged };
      }, "Failed to save pinned notifications"),
    [optimisticSave]
  );

  useEffect(() => {
    if (!collinsUser?._id) return;

    fetchUserPreferences(collinsUser._id)
      .then((userPreferences) => {
        if (userPreferences) {
          setPreferences(mergeWithDefaults(userPreferences));
        }
      })
      .catch((error) => {
        setHasLoadError(true);
        reportPreferencesLoadError(error);
      })
      .finally(() => {
        setHasLoadedPreferences(true);
      });
  }, [collinsUser?._id]);

  const contextValue = useMemo(
    () => ({
      preferences,
      isLoading,
      isSaving,
      hasLoadError,
      hasLoadedPreferences,
      loadPreferences,
      updateEvoFeedSubscription,
      updateAndSaveSubscription,
      updateAndSaveLocale,
      updateAndSaveLanguage,
      savePreferences,
      savePreset,
      applyPreset,
      deletePreset,
      renamePreset,
      updatePreset,
      setPresetPinned,
      reorderPresets,
      toggleEnquiryPinnedNotification,
      mergeEnquiryPinnedNotifications
    }),
    [
      preferences,
      isLoading,
      isSaving,
      hasLoadError,
      hasLoadedPreferences,
      loadPreferences,
      updateEvoFeedSubscription,
      updateAndSaveSubscription,
      updateAndSaveLocale,
      updateAndSaveLanguage,
      savePreferences,
      savePreset,
      applyPreset,
      deletePreset,
      renamePreset,
      updatePreset,
      setPresetPinned,
      reorderPresets,
      toggleEnquiryPinnedNotification,
      mergeEnquiryPinnedNotifications
    ]
  );

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
